import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useV1ProductionBomRevisionsRetrieve,
  useV1ProductionBomRevisionsPublishCreate,
  useV1ProductionBomItemsList,
  useV1ProductionBomItemsCreate,
  useV1ProductionBomItemsDestroy,
  useV1InventoryMaterialRevisionsList,
} from "@/api/generated/v1/v1";
import { usePermission } from "@/auth/usePermission";
import { StatusBadge } from "@/shared/components/StatusBadge";
import { PageBanner } from "@/shared/components/PageBanner";
import { PrintActionDialog } from "@/shared/components/printing/PrintActionDialog";
import { useQueryClient } from "@tanstack/react-query";

export function BomRevisionEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = usePermission("production.manage");

  const { data: revision, isLoading: revLoading } = useV1ProductionBomRevisionsRetrieve(id!);
  const { data: items, isLoading: itemsLoading } = useV1ProductionBomItemsList({
    bom_revision: id,
    page_size: 1000,
  } as any);

  const { mutateAsync: createItem, isPending: isAddingItem } = useV1ProductionBomItemsCreate();
  const { mutateAsync: deleteItem } = useV1ProductionBomItemsDestroy();
  const { mutateAsync: publishRevision, isPending: isPublishing } = useV1ProductionBomRevisionsPublishCreate();

  const [materialSearch, setMaterialSearch] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState<{
    id: string;
    name: string;
    sku: string;
    productionUom: string;
    stockUom: string;
  } | null>(null);

  const { data: materialResults, isFetching: isSearchingMaterial } = useV1InventoryMaterialRevisionsList(
    { search: materialSearch, status: "published", page_size: 50 } as any,
    { query: { enabled: materialSearch.length >= 2 } }
  );

  const [formData, setFormData] = useState({
    material_revision: "",
    quantity: "1",
    scrap_percent: "0",
    is_optional: false,
    sequence_no: "10",
  });

  const isDraft = (revision?.status as any) === "draft";

  // Calculate next sequence_no based on existing items
  const getNextSequenceNo = (currentItems?: any[]) => {
    if (!currentItems?.length) return 10;
    const maxSeq = Math.max(...currentItems.map((item: any) => item.sequence_no || 0));
    return maxSeq + 10;
  };

  useEffect(() => {
    if (items?.results) {
      const nextSeq = getNextSequenceNo(items.results);
      setFormData((prev) => {
        const currentSeq = parseInt(prev.sequence_no, 10);
        const isDup = items.results.some((i: any) => i.sequence_no === currentSeq);
        if (currentSeq <= 10 || isDup) {
          return { ...prev, sequence_no: String(nextSeq) };
        }
        return prev;
      });
    }
  }, [items]);

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isDraft) return;

    try {
      await createItem({
        data: {
          bom_revision: id!,
          material_revision: formData.material_revision,
          quantity: formData.quantity as any,
          scrap_percent: formData.scrap_percent as any,
          is_optional: formData.is_optional,
          sequence_no: parseInt(formData.sequence_no, 10),
        } as any,
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/v1/production/bom-items/"] });

      const newItems = items?.results ? [...items.results, { sequence_no: parseInt(formData.sequence_no, 10) }] : [];
      const nextSeq = getNextSequenceNo(newItems);

      setFormData({
        material_revision: "",
        quantity: "1",
        scrap_percent: "0",
        is_optional: false,
        sequence_no: String(nextSeq),
      });
      setSelectedMaterial(null);
      setMaterialSearch("");
    } catch (err: any) {
      console.error("Add BOM item error:", err);
      alert("خطا در افزودن قطعه: " + (err.response?.data?.detail || err.message || ""));
    }
  };

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isConfirmingPublish, setIsConfirmingPublish] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [customFieldValues, setCustomFieldValuesState] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!showPrintDialog || !revision) return;
    const familyDisplay = (revision as any).bom_family_display;
    const matRevId = familyDisplay?.output_material_revision_id || familyDisplay?.material_revision;
    const matFamId = familyDisplay?.output_material_family_id || familyDisplay?.material_family || familyDisplay?.material;

    import("@/features/customFields/api").then(async ({ getCustomFieldsForTarget }) => {
      let combinedValues: Record<string, any> = {};

      if (matFamId) {
        for (const ct of ["inventory.materialfamily", "inventory.material"]) {
          try {
            const res = await getCustomFieldsForTarget(ct, matFamId);
            if (res?.values) combinedValues = { ...combinedValues, ...res.values };
          } catch (e) {
            // Ignore
          }
        }
      }

      if (matRevId) {
        try {
          const res = await getCustomFieldsForTarget("inventory.materialrevision", matRevId);
          if (res?.values) combinedValues = { ...combinedValues, ...res.values };
        } catch (e) {
          console.error("Failed to load material revision custom fields:", e);
        }
      }

      try {
        const res = await getCustomFieldsForTarget("production.bomrevision", revision.id);
        if (res?.values) combinedValues = { ...combinedValues, ...res.values };
      } catch (e) {
        console.error("Failed to load bom revision custom fields:", e);
      }

      setCustomFieldValuesState(combinedValues);
    });
  }, [showPrintDialog, revision]);

  const handleDeleteItem = async (itemId: string) => {
    if (!isDraft) return;
    try {
      await deleteItem({ id: itemId });
      setDeletingId(null);
      await queryClient.invalidateQueries({ queryKey: ["/api/v1/production/bom-items/"] });
    } catch (err: any) {
      console.error("Delete BOM item error:", err);
      alert("خطا در حذف قطعه: " + (err.response?.data?.detail || err.message || "خطای غیرمنتظره"));
    }
  };

  const handlePublish = async () => {
    if (!isDraft) return;
    try {
      await publishRevision({ id: id!, data: {} as any });
      setIsConfirmingPublish(false);
      await queryClient.invalidateQueries({ queryKey: ["/api/v1/production/bom-revisions/"] });
      alert("نسخه با موفقیت منتشر شد.");
    } catch (err: any) {
      console.error("Publish BOM revision error:", err);
      alert("خطا در انتشار نسخه: " + (err.response?.data?.detail || err.message || ""));
    }
  };

  const getMaterialUom = (m: any): string => {
    return m.production_uom_code || m.stock_uom_code || m.family_uom_code || "";
  };

  if (revLoading) {
    return (
      <div style={{ padding: "3rem", textAlign: "center" }}>
        <span style={{ fontSize: "1.5rem" }}>⏳</span>
        <p style={{ color: "var(--color-text-muted, #666)", fontWeight: 700, marginTop: "0.5rem" }}>
          در حال بارگذاری اطلاعات نسخه BOM...
        </p>
      </div>
    );
  }

  if (!revision) {
    return (
      <div style={{ padding: "2rem" }}>
        <div
          style={{
            padding: "1.25rem",
            background: "rgba(239, 68, 68, 0.1)",
            borderRight: "4px solid #EF4444",
            borderRadius: "10px",
          }}
        >
          <p style={{ color: "#EF4444", fontWeight: 800, margin: 0 }}>نسخه BOM مورد نظر یافت نشد.</p>
        </div>
      </div>
    );
  }

  const seqNo = parseInt(formData.sequence_no, 10);
  const isDuplicateSeq = items?.results?.some((item: any) => item.sequence_no === seqNo);
  const familyDisplay = (revision as any).bom_family_display;
  const itemsList = items?.results || [];

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      {/* 1. STANDARDIZED PAGE BANNER */}
      <PageBanner
        title={`نسخه BOM: v${(revision as any).revision_no || (revision as any).revision_number}`}
        subtitle={`محصول: ${familyDisplay?.name || "—"} | تعریف مواد اولیه، قطعات و ضریب مصرف`}
        icon="⚙️"
        helpLink="/help/bom/revisions"
      >
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <button
            onClick={() => setShowPrintDialog(true)}
            style={{
              padding: "0.6rem 1.1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: "700",
              fontSize: "0.9rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>🖨️</span>
            <span>چاپ با قالب طراحی شده</span>
          </button>

          {isDraft && canManage && (
            isConfirmingPublish ? (
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  onClick={handlePublish}
                  disabled={isPublishing}
                  style={{
                    padding: "0.6rem 1.25rem",
                    borderRadius: "12px",
                    background: "#10B981",
                    color: "#ffffff",
                    fontWeight: "800",
                    fontSize: "0.9rem",
                    border: "none",
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(16, 185, 129, 0.3)",
                  }}
                >
                  {isPublishing ? "در حال انتشار..." : "تأیید انتشار نهایی"}
                </button>
                <button
                  onClick={() => setIsConfirmingPublish(false)}
                  disabled={isPublishing}
                  style={{
                    padding: "0.6rem 1rem",
                    borderRadius: "12px",
                    background: "rgba(255, 255, 255, 0.2)",
                    color: "#ffffff",
                    fontWeight: "700",
                    fontSize: "0.9rem",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  انصراف
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsConfirmingPublish(true)}
                disabled={isPublishing || !itemsList.length}
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: "12px",
                  background: "#ffffff",
                  color: "#007270",
                  fontWeight: "800",
                  fontSize: "0.9rem",
                  border: "1px solid rgba(255, 255, 255, 0.8)",
                  cursor: itemsList.length ? "pointer" : "not-allowed",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  opacity: itemsList.length ? 1 : 0.6,
                }}
              >
                <span>✓</span>
                <span>انتشار نهایی (Publish)</span>
              </button>
            )
          )}

          <button
            onClick={() => navigate(`/bom/families/${revision.family}`)}
            style={{
              padding: "0.6rem 1.1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: "700",
              fontSize: "0.9rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>←</span>
            <span>بازگشت به خانواده</span>
          </button>
        </div>
      </PageBanner>

      {/* 2. STATS REPORT GRID (TOP SUMMARY CARDS) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem",
        }}
      >
        {/* CARD 1: REVISION STATUS */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(0,114,112,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            📌
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              وضعیت نگارش
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <StatusBadge status={revision.status as string} />
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#007270" }}>وضعیت فعلی</span>
            </div>
          </div>
        </div>

        {/* CARD 2: ITEMS COUNT */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(59,130,246,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            🧩
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              تعداد قطعات و مواد
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.3rem", fontWeight: "800", color: "#3B82F6" }}>
                {itemsLoading ? "..." : itemsList.length.toLocaleString("fa-IR")}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#3B82F6" }}>اقلام تعریف‌شده</span>
            </div>
          </div>
        </div>

        {/* CARD 3: OUTPUT PRODUCT */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(245,158,11,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            📦
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              محصول خروجی
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1rem", fontWeight: "800", color: "#D4AF37" }}>
                {familyDisplay?.name || "مشخص نشده"}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#D4AF37" }}>محصول مرتبط</span>
            </div>
          </div>
        </div>

        {/* CARD 4: REVISION NOTES */}
        <div className="brand-card kpi-card">
          <span
            style={{
              fontSize: "1.8rem",
              background: "rgba(16,185,129,0.08)",
              width: "48px",
              height: "48px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            📝
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              یادداشت نسخه
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.9rem", fontWeight: "700", color: "#10B981" }}>
                {(revision as any).notes || (revision as any).revision_note || "بدون یادداشت"}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981" }}>اطلاعات</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. MAIN CONTENT LAYOUT: ITEMS TABLE + ADD ITEM FORM */}
      <div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start", flexWrap: "wrap" }}>
        
        {/* ====== ITEMS TABLE CARD ====== */}
        <div className="brand-card-extand" style={{ flex: 1, padding: "1.5rem", minWidth: "320px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
              marginBottom: "1.25rem",
              borderBottom: "1px solid var(--color-border, #E4DCC8)",
              paddingBottom: "0.75rem",
            }}
          >
            <span style={{ fontSize: "1.3rem" }}>📋</span>
            <div>
              <h2 style={{ fontSize: "1.05rem", fontWeight: "800", color: "#007270", margin: 0 }}>
                لیست قطعات و مواد اولیه تشکیل‌دهنده (BOM Items)
              </h2>
              <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted, #666)" }}>
                فرمول ساخت محصول شامل مقدار مصرف، واحد و درصد ضایعات/پرت
              </span>
            </div>
          </div>

          {itemsLoading ? (
            <div style={{ padding: "3rem", textAlign: "center" }}>
              <span style={{ fontSize: "1.5rem" }}>⏳</span>
              <p style={{ color: "var(--color-text-muted, #666)", fontWeight: 700, marginTop: "0.5rem" }}>
                در حال بارگذاری لیست قطعات...
              </p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--color-border, #E4DCC8)", color: "#007270" }}>
                    <th style={{ padding: "0.85rem 0.75rem", fontWeight: "800", fontSize: "0.88rem" }}>ردیف</th>
                    <th style={{ padding: "0.85rem 0.75rem", fontWeight: "800", fontSize: "0.88rem" }}>کد قطعه (SKU)</th>
                    <th style={{ padding: "0.85rem 0.75rem", fontWeight: "800", fontSize: "0.88rem" }}>نام قطعه / متریال</th>
                    <th style={{ padding: "0.85rem 0.75rem", fontWeight: "800", fontSize: "0.88rem", textAlign: "center" }}>مقدار مصرف</th>
                    <th style={{ padding: "0.85rem 0.75rem", fontWeight: "800", fontSize: "0.88rem", textAlign: "center" }}>واحد (UOM)</th>
                    <th style={{ padding: "0.85rem 0.75rem", fontWeight: "800", fontSize: "0.88rem", textAlign: "center" }}>پرت (%)</th>
                    <th style={{ padding: "0.85rem 0.75rem", fontWeight: "800", fontSize: "0.88rem", textAlign: "center" }}>اختیاری</th>
                    {isDraft && <th style={{ padding: "0.85rem 0.75rem", fontWeight: "800", fontSize: "0.88rem", textAlign: "center" }}>عملیات</th>}
                  </tr>
                </thead>
                <tbody>
                  {itemsList.length === 0 ? (
                    <tr>
                      <td colSpan={isDraft ? 8 : 7} style={{ padding: "3.5rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                        <div style={{ fontSize: "2.2rem", marginBottom: "0.5rem" }}>📋</div>
                        <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                          هنوز هیچ قطعه یا متریالی به این نسخه BOM اضافه نشده است.
                        </span>
                      </td>
                    </tr>
                  ) : (
                    itemsList.map((item: any) => (
                      <tr
                        key={item.id}
                        style={{
                          borderBottom: "1px solid var(--color-border, #E4DCC8)",
                          transition: "background 0.2s ease",
                        }}
                      >
                        <td style={{ padding: "0.85rem 0.75rem", color: "var(--color-text-muted)", fontWeight: 700, fontSize: "0.85rem" }}>
                          {item.sequence_no}
                        </td>
                        <td style={{ padding: "0.85rem 0.75rem" }}>
                          <span
                            dir="ltr"
                            style={{
                              fontFamily: "monospace",
                              fontWeight: 800,
                              color: "#007270",
                              background: "rgba(0, 114, 112, 0.08)",
                              padding: "0.25rem 0.6rem",
                              borderRadius: "6px",
                              fontSize: "0.85rem",
                              display: "inline-block",
                            }}
                          >
                            {item.material_revision_display?.sku || item.material_revision_display?.code || "—"}
                          </span>
                        </td>
                        <td style={{ padding: "0.85rem 0.75rem" }}>
                          <div style={{ fontWeight: 800, color: "var(--color-text-main, #2B2B2B)", fontSize: "0.92rem" }}>
                            {item.material_revision_display?.name || "—"}
                            {item.is_optional && (
                              <span
                                style={{
                                  marginRight: "0.5rem",
                                  fontSize: "0.72rem",
                                  fontWeight: 800,
                                  color: "#D4AF37",
                                  background: "rgba(245, 158, 11, 0.12)",
                                  padding: "0.15rem 0.45rem",
                                  borderRadius: "6px",
                                  border: "1px solid rgba(245, 158, 11, 0.2)",
                                }}
                              >
                                اختیاری
                              </span>
                            )}
                          </div>
                          {item.material_revision_display?.revision_no && (
                            <div style={{ fontSize: "0.78rem", color: "var(--color-text-muted)", marginTop: "0.15rem", fontWeight: 600 }}>
                              نسخه متریال {item.material_revision_display.revision_no}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "0.85rem 0.75rem", textAlign: "center", fontWeight: 800, fontSize: "1.05rem", color: "#007270" }}>
                          {item.quantity}
                        </td>
                        <td style={{ padding: "0.85rem 0.75rem", textAlign: "center" }}>
                          {item.production_uom_display ? (
                            <span
                              style={{
                                background: "rgba(0, 114, 112, 0.1)",
                                color: "#007270",
                                padding: "0.2rem 0.55rem",
                                borderRadius: "6px",
                                fontSize: "0.82rem",
                                fontWeight: 800,
                              }}
                            >
                              {item.production_uom_display}
                            </span>
                          ) : (
                            <span style={{ color: "#9CA3AF", fontSize: "0.82rem" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "0.85rem 0.75rem", textAlign: "center" }}>
                          {item.scrap_percent > 0 ? (
                            <span style={{ color: "#D4AF37", fontWeight: 800, fontSize: "0.88rem" }}>
                              %{item.scrap_percent}
                            </span>
                          ) : (
                            <span style={{ color: "#9CA3AF", fontSize: "0.82rem" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "0.85rem 0.75rem", textAlign: "center" }}>
                          {item.is_optional ? (
                            <span style={{ color: "#D4AF37", fontWeight: 800, fontSize: "1rem" }}>✓</span>
                          ) : (
                            <span style={{ color: "#9CA3AF", fontSize: "0.82rem" }}>—</span>
                          )}
                        </td>
                        {isDraft && (
                          <td style={{ padding: "0.85rem 0.75rem", textAlign: "center" }}>
                            {deletingId === item.id ? (
                              <div style={{ display: "flex", gap: "0.3rem", justifyContent: "center" }}>
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  style={{
                                    color: "#FFFFFF",
                                    background: "#EF4444",
                                    border: "none",
                                    borderRadius: "6px",
                                    fontSize: "0.78rem",
                                    padding: "0.25rem 0.55rem",
                                    fontWeight: 800,
                                    cursor: "pointer",
                                  }}
                                >
                                  تأیید
                                </button>
                                <button
                                  onClick={() => setDeletingId(null)}
                                  style={{
                                    border: "1px solid var(--color-border)",
                                    background: "#FFFFFF",
                                    borderRadius: "6px",
                                    fontSize: "0.78rem",
                                    padding: "0.25rem 0.55rem",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                  }}
                                >
                                  انصراف
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeletingId(item.id)}
                                style={{
                                  color: "#EF4444",
                                  borderColor: "#EF4444",
                                  border: "1px solid #EF4444",
                                  background: "transparent",
                                  borderRadius: "6px",
                                  fontSize: "0.8rem",
                                  padding: "0.25rem 0.65rem",
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                حذف
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ====== ADD ITEM FORM CARD (DRAFT ONLY) ====== */}
        {isDraft && canManage && (
          <div
            className="brand-card-extand"
            style={{
              width: "340px",
              flexShrink: 0,
              padding: "1.5rem",
              height: "fit-content",
              position: "sticky",
              top: "1.5rem",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "1.25rem",
                borderBottom: "1px solid var(--color-border, #E4DCC8)",
                paddingBottom: "0.75rem",
              }}
            >
              <span style={{ fontSize: "1.2rem" }}>➕</span>
              <h2 style={{ fontSize: "1rem", fontWeight: "800", color: "#007270", margin: 0 }}>
                افزودن قطعه / متریال جدید
              </h2>
            </div>

            <form onSubmit={handleAddItem} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {/* Material Search */}
              <div>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.83rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                  انتخاب متریال / قطعه <span style={{ color: "#EF4444" }}>*</span>
                </label>

                {selectedMaterial ? (
                  <div
                    style={{
                      padding: "0.75rem",
                      border: "1px solid #007270",
                      borderRadius: "10px",
                      background: "rgba(0, 114, 112, 0.06)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: "0.9rem", display: "block", color: "#007270" }}>
                          {selectedMaterial.name}
                        </strong>
                        {selectedMaterial.sku && (
                          <span dir="ltr" style={{ color: "var(--color-text-muted)", fontSize: "0.78rem", fontFamily: "monospace" }}>
                            SKU: {selectedMaterial.sku}
                          </span>
                        )}
                        <div style={{ marginTop: "0.35rem", display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                          {selectedMaterial.productionUom && (
                            <span
                              style={{
                                fontSize: "0.72rem",
                                background: "rgba(0, 114, 112, 0.15)",
                                color: "#007270",
                                padding: "0.15rem 0.4rem",
                                borderRadius: "4px",
                                fontWeight: 800,
                              }}
                            >
                              واحد تولید: {selectedMaterial.productionUom}
                            </span>
                          )}
                          {selectedMaterial.stockUom && selectedMaterial.stockUom !== selectedMaterial.productionUom && (
                            <span
                              style={{
                                fontSize: "0.72rem",
                                background: "rgba(100,100,100,0.1)",
                                color: "var(--color-text-muted)",
                                padding: "0.15rem 0.4rem",
                                borderRadius: "4px",
                                fontWeight: 700,
                              }}
                            >
                              واحد انبار: {selectedMaterial.stockUom}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedMaterial(null);
                          setFormData({ ...formData, material_revision: "" });
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#EF4444",
                          fontSize: "0.78rem",
                          fontWeight: "800",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        تغییر
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      className="form-input-control"
                      style={{ fontSize: "0.85rem", padding: "0.55rem 0.75rem" }}
                      value={materialSearch}
                      onChange={(e) => setMaterialSearch(e.target.value)}
                      placeholder="جستجو با نام یا SKU (حداقل ۲ حرف)..."
                      autoComplete="off"
                    />
                    {isSearchingMaterial && (
                      <p style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginTop: "0.25rem", fontWeight: 600 }}>
                        ⏳ در حال جستجوی متریال...
                      </p>
                    )}
                    {materialResults?.results && materialResults.results.length > 0 && (
                      <div
                        style={{
                          marginTop: "0.4rem",
                          border: "1px solid var(--color-border, #E4DCC8)",
                          borderRadius: "10px",
                          maxHeight: "220px",
                          overflowY: "auto",
                          background: "#FFFFFF",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      >
                        {materialResults.results.map((m: any) => {
                          const uom = getMaterialUom(m);
                          return (
                            <div
                              key={m.id}
                              onClick={() => {
                                setSelectedMaterial({
                                  id: m.id,
                                  name: m.name,
                                  sku: m.sku || "",
                                  productionUom: m.production_uom_code || "",
                                  stockUom: m.stock_uom_code || m.family_uom_code || "",
                                });
                                setFormData({ ...formData, material_revision: m.id });
                                setMaterialSearch("");
                              }}
                              style={{
                                padding: "0.6rem 0.75rem",
                                cursor: "pointer",
                                borderBottom: "1px solid var(--color-border, #E4DCC8)",
                                transition: "background 0.15s ease",
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0, 114, 112, 0.06)")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                            >
                              <div style={{ fontWeight: 800, fontSize: "0.85rem", color: "#2B2B2B" }}>{m.name}</div>
                              <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.2rem", flexWrap: "wrap" }}>
                                {m.sku && (
                                  <span dir="ltr" style={{ color: "var(--color-text-muted)", fontSize: "0.75rem", fontFamily: "monospace" }}>
                                    {m.sku}
                                  </span>
                                )}
                                {uom && (
                                  <span style={{ fontSize: "0.75rem", color: "#007270", fontWeight: 700 }}>
                                    واحد: {uom}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Quantity */}
              <div>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.83rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                  مقدار مصرف <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    required
                    className="form-input-control"
                    style={{ fontSize: "0.9rem", padding: "0.55rem 0.75rem", flex: 1, textAlign: "center" }}
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    dir="ltr"
                  />
                  {selectedMaterial && (selectedMaterial.productionUom || selectedMaterial.stockUom) && (
                    <span
                      style={{
                        fontSize: "0.82rem",
                        fontWeight: 800,
                        color: "#007270",
                        background: "rgba(0, 114, 112, 0.1)",
                        padding: "0.45rem 0.7rem",
                        borderRadius: "8px",
                        whiteSpace: "nowrap",
                        border: "1px solid rgba(0, 114, 112, 0.2)",
                      }}
                    >
                      {selectedMaterial.productionUom || selectedMaterial.stockUom}
                    </span>
                  )}
                </div>
              </div>

              {/* Scrap Percent */}
              <div>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.83rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                  درصد پرت (Scrap %)
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    max="100"
                    className="form-input-control"
                    style={{ fontSize: "0.9rem", padding: "0.55rem 0.75rem", flex: 1, textAlign: "center" }}
                    value={formData.scrap_percent}
                    onChange={(e) => setFormData({ ...formData, scrap_percent: e.target.value })}
                    dir="ltr"
                  />
                  <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", fontWeight: 700 }}>%</span>
                </div>
              </div>

              {/* Sequence */}
              <div>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.83rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                  ترتیب r (Sequence)
                </label>
                <input
                  type="number"
                  className="form-input-control"
                  style={{ fontSize: "0.9rem", padding: "0.55rem 0.75rem", textAlign: "center" }}
                  value={formData.sequence_no}
                  onChange={(e) => setFormData({ ...formData, sequence_no: e.target.value })}
                  dir="ltr"
                />
                {isDuplicateSeq && (
                  <p style={{ fontSize: "0.78rem", color: "#EF4444", marginTop: "0.3rem", fontWeight: 700 }}>
                    ⚠️ شماره ترتیب تکراری است
                  </p>
                )}
              </div>

              {/* Optional */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.2rem" }}>
                <input
                  type="checkbox"
                  id="is_optional"
                  checked={formData.is_optional}
                  onChange={(e) => setFormData({ ...formData, is_optional: e.target.checked })}
                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                />
                <label htmlFor="is_optional" style={{ fontSize: "0.85rem", fontWeight: 700, cursor: "pointer", color: "#2B2B2B" }}>
                  قطعه اختیاری (Optional)
                </label>
              </div>

              <button
                type="submit"
                disabled={isAddingItem || !formData.material_revision || isDuplicateSeq}
                style={{
                  padding: "0.65rem 1.2rem",
                  borderRadius: "10px",
                  background: "#007270",
                  color: "#FFFFFF",
                  border: "none",
                  fontSize: "0.9rem",
                  fontWeight: 800,
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0, 114, 112, 0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.4rem",
                  marginTop: "0.5rem",
                  opacity: !formData.material_revision || isDuplicateSeq ? 0.6 : 1,
                  transition: "all 0.2s ease",
                }}
              >
                <span>{isAddingItem ? "⏳" : "➕"}</span>
                <span>{isAddingItem ? "در حال افزودن..." : "افزودن قطعه"}</span>
              </button>
            </form>
          </div>
        )}
      </div>

      {showPrintDialog && (
        <PrintActionDialog
          reportType="production.bomrevision"
          data={{
            id: revision.id,
            revision_no: (revision as any).revision_no || (revision as any).revision_number,
            status: revision.status,
            bom_family: familyDisplay?.name || "",
            bom_code: familyDisplay?.code || "",
            commercial_name: customFieldValues.commercial_name || familyDisplay?.name || "",
            product_type: customFieldValues.product_type || customFieldValues.vehicle_category || (revision as any).product_type || "",
            product_variant: customFieldValues.product_variant || customFieldValues.kind || (revision as any).product_variant || "",
            product_model: customFieldValues.product_model || customFieldValues.model || (revision as any).model || "",
            model: customFieldValues.product_model || customFieldValues.model || (revision as any).model || "",
            kind: customFieldValues.product_variant || customFieldValues.kind || (revision as any).product_variant || "",
            type: customFieldValues.product_type || customFieldValues.vehicle_category || (revision as any).product_type || "",
            document_number: familyDisplay?.code ? `NCY-${familyDisplay.code}-DN-00-00` : "NCY-P6-DN-00-00",
            ...customFieldValues,
            items: (Array.isArray(items) ? items : (items as any)?.results ?? []).map((item: any, idx: number) => ({
              ...item,
              sequence_no: item.sequence_no ?? (idx + 1) * 10,
              item_name: item.material_revision_display?.name || item.material_revision_name || item.item_name || "",
              material_name: item.material_revision_display?.name || item.material_revision_name || item.item_name || "",
              part_code: item.material_revision_display?.code || item.material_revision_sku || item.part_code || "",
              group_name: item.group_name || item.material_revision_display?.group_name || item.part_group || "قطعات استاندارد",
              part_group: item.part_group || item.group_name || item.material_revision_display?.group_name || "قطعات استاندارد",
              supplier: item.supplier || item.manufacturer_supplier || "تولیدکننده داخلی",
              manufacturer_supplier: item.manufacturer_supplier || item.supplier || "تولیدکننده داخلی",
              brand: item.brand || item.raw_commercial_name || item.material_revision_display?.name || "",
              raw_commercial_name: item.raw_commercial_name || item.brand || item.material_revision_display?.name || "",
              has_national_standard: item.has_national_standard ?? 1,
              quantity: item.quantity ?? 1,
              scrap_percent: item.scrap_percent ?? 0,
              remarks: item.remarks || item.notes || "-",
            })),
          }}
          onClose={() => setShowPrintDialog(false)}
        />
      )}
    </div>
  );
}
